import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, CheckCircle, X } from "lucide-react";

const CompleteProofModal = ({ isOpen, taskTitle, isRecurring = false, onClose, onConfirm }) => {
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);

    const reset = () => {
        setFile(null);
        setPreview(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleFile = (e) => {
        const picked = e.target.files?.[0];
        if (!picked) return;
        setFile(picked);
        setPreview(URL.createObjectURL(picked));
    };

    const handleConfirm = () => {
        onConfirm(file);
        reset();
        onClose();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 flex items-end sm:items-center justify-center bg-black/50 z-[60] p-4"
                    onClick={handleClose}
                >
                    <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 40, opacity: 0 }}
                        className="bg-surface w-full max-w-sm rounded-2xl shadow-2xl border border-line p-5"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between gap-2 mb-3">
                            <div>
                                <h3 className="text-base font-semibold text-ink">Complete task?</h3>
                                {taskTitle && (
                                    <p className="text-sm text-ink-muted mt-0.5 line-clamp-2">
                                        {taskTitle}
                                    </p>
                                )}
                                {isRecurring && (
                                    <p className="text-[11px] text-primary mt-1">
                                        Repeating task — next occurrence will be scheduled automatically.
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={handleClose}
                                className="p-2 rounded-lg hover:bg-surface-2 text-ink-muted"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={handleFile}
                        />

                        {preview ? (
                            <div className="relative mb-4 rounded-xl overflow-hidden border border-line">
                                <img
                                    src={preview}
                                    alt="Proof preview"
                                    className="w-full max-h-40 object-cover"
                                />
                                <button
                                    type="button"
                                    onClick={reset}
                                    className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 text-white"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => fileRef.current?.click()}
                                className="w-full mb-4 flex items-center justify-center gap-2 py-4 rounded-xl border-2 border-dashed border-line text-ink-secondary hover:border-primary hover:text-primary transition min-h-[44px]"
                            >
                                <Camera size={20} />
                                <span className="text-sm font-medium">Add photo proof (optional)</span>
                            </button>
                        )}

                        <div className="flex flex-col gap-2">
                            <button
                                type="button"
                                onClick={handleConfirm}
                                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-success text-white font-semibold text-sm min-h-[44px] hover:opacity-90 transition"
                            >
                                <CheckCircle size={18} />
                                {file ? "Complete with photo" : "Mark complete"}
                            </button>
                            {!preview && (
                                <button
                                    type="button"
                                    onClick={() => fileRef.current?.click()}
                                    className="w-full py-2.5 text-sm text-primary font-medium min-h-[44px]"
                                >
                                    Attach photo first
                                </button>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default CompleteProofModal;
